import { useState } from 'react';
import type {
  AdminPriceEdit,
  AdminPriceInput,
  AdminPriceRow,
  AdminProductInput,
  AdminProductRow,
} from '@st/contract';
import {
  useAdminProducts,
  useReplacePrice,
  useSavePrice,
  useSaveProduct,
  useStripeSync,
  useStripeSyncPreview,
  useTrainerPrograms,
} from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { toMutationMessage } from '../../i18n/errorMessage';
import { formatMoney } from '../../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  DataTable,
  Input,
  Modal,
  NumberInput,
  Select,
  SkeletonList,
  Switch,
  Textarea,
  useToast,
} from '../../ui';

const INTERVAL_KEY = {
  one_time: 'pricing.oneTime',
  month: 'pricing.perMonth',
  quarter: 'pricing.perQuarter',
  year: 'pricing.perYear',
} as const;

/**
 * Razdoblje naplate iz `interval` I `intervalCount`.
 *
 * Stripe kvartalnu cijenu zapisuje kao „mjesec × 3", ne kao vlastiti interval.
 * Tablica je prije gledala samo `interval`, pa je kvartalna cijena pisala
 * „mjesečno" — tri puta manje nego što se stvarno naplaćuje. Godišnja je bila
 * jednako pogrešno mapirana.
 */
function billingLabelKey(price: { interval: AdminPriceRow['interval']; intervalCount: number }) {
  if (price.interval === 'month' && price.intervalCount === 3) return 'pricing.perQuarter';
  if (price.interval === 'month' && price.intervalCount === 12) return 'pricing.perYear';
  return INTERVAL_KEY[price.interval];
}

/** Cijene se unose u eurima, a zapisuju u centima — novac ne putuje decimalno. */
const eurosToCents = (v: number) => Math.round(v * 100);
const centsToEuros = (v: number) => v / 100;

const MODE_KEY = {
  online: 'product.modeOnline',
  gym: 'product.modeGym',
  hybrid: 'product.modeHybrid',
} as const;

/** Što se trenutno uređuje: cijena se dodaje, mijenja ili joj se mijenja iznos. */
type PriceEditing =
  | { mode: 'create'; productId: string }
  | { mode: 'edit'; price: AdminPriceRow }
  | { mode: 'replace'; price: AdminPriceRow }
  | null;

export function AdminProducts() {
  const { t, locale } = useTranslation();
  const { toast } = useToast();
  const query = useAdminProducts();
  const save = useSaveProduct();
  const savePrice = useSavePrice();
  const replacePrice = useReplacePrice();

  const [editing, setEditing] = useState<AdminProductRow | 'new' | null>(null);
  const [priceEditing, setPriceEditing] = useState<PriceEditing>(null);

  return (
    <>
      <PageHeader
        eyebrow={t('shell.admin')}
        title={t('admin.products')}
        action={
          <Button size="sm" onClick={() => setEditing('new')}>
            {t('product.newTitle')}
          </Button>
        }
      />

      <StripeSyncCard />
      <QueryBoundary
        query={query}
        skeleton={<SkeletonList rows={3} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{
          message: t('empty.noProducts'),
          actionLabel: t('product.newTitle'),
          onAction: () => setEditing('new'),
        }}
      >
        {(data) => (
          <div className="flex flex-col gap-5">
            {data.items.map((p) => (
              <Card key={p.id} as="section">
                <CardHeader
                  eyebrow={`${p.slug} · ${t(MODE_KEY[p.deliveryMode])}`}
                  title={p.nameHr}
                  action={
                    <div className="flex items-center gap-2">
                      {p.highlighted && <Badge tone="accent">{t('pricing.popular')}</Badge>}
                      <Badge tone={p.active ? 'success' : 'neutral'}>
                        {p.active ? t('admin.active') : t('admin.inactive')}
                      </Badge>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>
                        {t('action.edit')}
                      </Button>
                    </div>
                  }
                />

                {p.accessDurationDays !== null && (
                  <p className="mb-3 text-[13px] text-ink-muted">
                    {t('product.accessDurationValue', { n: p.accessDurationDays })}
                  </p>
                )}

                {(p.featuresHrJson ?? []).length > 0 && (
                  <ul className="mb-4 flex flex-wrap gap-2">
                    {(p.featuresHrJson ?? []).map((f) => (
                      <li key={f}>
                        <Badge tone="neutral">{f}</Badge>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="eyebrow">{t('admin.price')}</p>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setPriceEditing({ mode: 'create', productId: p.id })}
                  >
                    {t('price.add')}
                  </Button>
                </div>

                {p.prices.length === 0 ? (
                  <p className="text-[13px] text-ink-muted">{t('product.noPrices')}</p>
                ) : (
                  <DataTable
                    caption={`${p.nameHr} — ${t('admin.price')}`}
                    rows={p.prices}
                    rowKey={(r) => r.id}
                    columns={[
                      {
                        key: 'amount',
                        header: t('admin.price'),
                        render: (r) => formatMoney(r.amountCents, r.currency, locale),
                      },
                      {
                        key: 'compare',
                        header: t('admin.compareAt'),
                        render: (r) =>
                          r.compareAtCents ? (
                            <span className="text-ink-muted line-through">
                              {formatMoney(r.compareAtCents, r.currency, locale)}
                            </span>
                          ) : (
                            '—'
                          ),
                        hideOnMobile: true,
                      },
                      {
                        key: 'interval',
                        header: t('admin.interval'),
                        render: (r) => t(billingLabelKey(r)),
                      },
                      {
                        key: 'active',
                        header: t('admin.active'),
                        render: (r) => (
                          <span className="flex flex-wrap items-center gap-2">
                            <Badge tone={r.active ? 'success' : 'neutral'}>
                              {r.active ? t('admin.active') : t('admin.inactive')}
                            </Badge>
                            {/*
                              Cijena postoji kod nas prije nego u Stripeu. Dok
                              nije ondje, na cjeniku se vidi ali se ne može
                              kupiti — i to mora pisati, a ne se otkriti tek
                              kad kupac klikne.
                            */}
                            <Badge tone={r.stripePriceId ? 'success' : 'warning'}>
                              {r.stripePriceId ? t('price.inStripe') : t('price.notInStripe')}
                            </Badge>
                          </span>
                        ),
                      },
                      {
                        key: 'actions',
                        header: t('action.edit'),
                        align: 'right',
                        render: (r) => (
                          <span className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setPriceEditing({ mode: 'edit', price: r })}
                            >
                              {t('price.edit')}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setPriceEditing({ mode: 'replace', price: r })}
                            >
                              {t('price.replace')}
                            </Button>
                          </span>
                        ),
                      },
                    ]}
                  />
                )}
              </Card>
            ))}
          </div>
        )}
      </QueryBoundary>

      {priceEditing && (
        <PriceFormModal
          editing={priceEditing}
          pending={savePrice.isPending || replacePrice.isPending}
          onClose={() => setPriceEditing(null)}
          onSubmit={(input) => {
            const done = (msg: string) => () => {
              setPriceEditing(null);
              toast(msg, 'success');
            };
            const failed = (error: unknown) => toast(toMutationMessage(t, error), 'danger');

            if (priceEditing.mode === 'replace') {
              replacePrice.mutate(
                { priceId: priceEditing.price.id, input: input as AdminPriceInput },
                { onSuccess: done(t('price.replaced')), onError: failed },
              );
              return;
            }
            if (priceEditing.mode === 'edit') {
              savePrice.mutate(
                { mode: 'edit', priceId: priceEditing.price.id, input: input as AdminPriceEdit },
                { onSuccess: done(t('price.saved')), onError: failed },
              );
              return;
            }
            savePrice.mutate(
              { mode: 'create', productId: priceEditing.productId, input: input as AdminPriceInput },
              { onSuccess: done(t('price.saved')), onError: failed },
            );
          }}
        />
      )}

      {editing && (
        <ProductFormModal
          product={editing === 'new' ? null : editing}
          pending={save.isPending}
          onClose={() => setEditing(null)}
          onSubmit={(input) =>
            save.mutate(
              { ...(editing === 'new' ? {} : { id: editing.id }), input },
              {
                onSuccess: () => {
                  setEditing(null);
                  toast(t('product.saved'), 'success');
                },
                onError: (error) => toast(toMutationMessage(t, error), 'danger'),
              },
            )
          }
        />
      )}
    </>
  );
}

const linesToList = (value: string): string[] =>
  value
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

function ProductFormModal({
  product,
  pending,
  onClose,
  onSubmit,
}: {
  product: AdminProductRow | null;
  pending: boolean;
  onClose: () => void;
  onSubmit: (input: AdminProductInput) => void;
}) {
  const { t } = useTranslation();
  // Popis planova za vezu paket → program. Bez te veze klijent nakon kupnje ne
  // dobiva nikakav trening, a to se prije nije moglo postaviti iz sučelja.
  const programs = useTrainerPrograms();
  const [slug, setSlug] = useState(product?.slug ?? '');
  const [nameHr, setNameHr] = useState(product?.nameHr ?? '');
  const [nameEn, setNameEn] = useState(product?.nameEn ?? '');
  const [descriptionHr, setDescriptionHr] = useState(product?.descriptionHr ?? '');
  const [descriptionEn, setDescriptionEn] = useState(product?.descriptionEn ?? '');
  const [featuresHr, setFeaturesHr] = useState((product?.featuresHrJson ?? []).join('\n'));
  const [featuresEn, setFeaturesEn] = useState((product?.featuresEnJson ?? []).join('\n'));
  const [accessDays, setAccessDays] = useState<number | ''>(product?.accessDurationDays ?? '');
  const [deliveryMode, setDeliveryMode] = useState<AdminProductRow['deliveryMode']>(
    product?.deliveryMode ?? 'online',
  );
  const [trialWorkouts, setTrialWorkouts] = useState<number | ''>(product?.trialWorkoutCount ?? 0);
  const [programId, setProgramId] = useState(product?.programId ?? '');
  const [sortOrder, setSortOrder] = useState<number | ''>(product?.sortOrder ?? 0);
  const [highlighted, setHighlighted] = useState(product?.highlighted ?? false);
  const [active, setActive] = useState(product?.active ?? true);
  const [errors, setErrors] = useState<{ slug?: string; nameHr?: string; nameEn?: string }>({});

  function submit() {
    const next: { slug?: string; nameHr?: string; nameEn?: string } = {};
    if (slug.trim().length < 2) next.slug = t('product.slugRequired');
    if (!nameHr.trim()) next.nameHr = t('product.nameHrRequired');
    if (!nameEn.trim()) next.nameEn = t('product.nameEnRequired');
    setErrors(next);
    if (next.slug || next.nameHr || next.nameEn) return;

    onSubmit({
      slug: slug.trim(),
      nameHr: nameHr.trim(),
      nameEn: nameEn.trim(),
      descriptionHr: descriptionHr.trim() || null,
      descriptionEn: descriptionEn.trim() || null,
      featuresHr: linesToList(featuresHr),
      featuresEn: linesToList(featuresEn),
      accessDurationDays: accessDays === '' ? null : accessDays,
      deliveryMode,
      trialWorkoutCount: trialWorkouts === '' ? 0 : trialWorkouts,
      programId: programId || null,
      sortOrder: sortOrder === '' ? 0 : sortOrder,
      highlighted,
      active,
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={product ? t('product.editTitle') : t('product.newTitle')}
      closeLabel={t('a11y.closeDialog')}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" loading={pending} onClick={submit}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Input
          id="product-slug"
          label={t('product.slug')}
          value={slug}
          required
          error={errors.slug}
          onChange={(e) => setSlug(e.target.value)}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            id="product-name-hr"
            label={t('product.nameHr')}
            value={nameHr}
            required
            error={errors.nameHr}
            onChange={(e) => setNameHr(e.target.value)}
          />
          <Input
            id="product-name-en"
            label={t('product.nameEn')}
            value={nameEn}
            required
            error={errors.nameEn}
            onChange={(e) => setNameEn(e.target.value)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Textarea
            id="product-description-hr"
            label={t('product.descriptionHr')}
            rows={3}
            value={descriptionHr}
            onChange={(e) => setDescriptionHr(e.target.value)}
          />
          <Textarea
            id="product-description-en"
            label={t('product.descriptionEn')}
            rows={3}
            value={descriptionEn}
            onChange={(e) => setDescriptionEn(e.target.value)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Textarea
            id="product-features-hr"
            label={t('product.featuresHr')}
            hint={t('product.featuresHelp')}
            rows={4}
            value={featuresHr}
            onChange={(e) => setFeaturesHr(e.target.value)}
          />
          <Textarea
            id="product-features-en"
            label={t('product.featuresEn')}
            hint={t('product.featuresHelp')}
            rows={4}
            value={featuresEn}
            onChange={(e) => setFeaturesEn(e.target.value)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <NumberInput
            id="product-access-days"
            label={t('product.accessDurationDays')}
            hint={t('product.accessDurationHelp')}
            value={accessDays}
            min={1}
            onValueChange={setAccessDays}
          />
          <Select
            id="product-delivery-mode"
            label={t('product.deliveryMode')}
            value={deliveryMode}
            options={(['online', 'gym', 'hybrid'] as const).map((v) => ({
              value: v,
              label: t(MODE_KEY[v]),
            }))}
            onChange={(e) =>
              setDeliveryMode(e.target.value as AdminProductRow['deliveryMode'])
            }
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <NumberInput
            id="product-trial-workouts"
            label={t('product.trialWorkouts')}
            hint={t('product.trialWorkoutsHelp')}
            value={trialWorkouts}
            min={0}
            onValueChange={setTrialWorkouts}
          />
          <NumberInput
            id="product-sort-order"
            label={t('product.sortOrder')}
            hint={t('product.sortOrderHelp')}
            value={sortOrder}
            min={0}
            onValueChange={setSortOrder}
          />
        </div>

        <Select
          id="product-program"
          label={t('product.program')}
          hint={t('product.programHelp')}
          value={programId}
          options={[
            { value: '', label: t('product.programNone') },
            ...(programs.data?.items ?? []).map((p) => ({ value: p.id, label: p.nameHr })),
          ]}
          onChange={(e) => setProgramId(e.target.value)}
        />

        <Switch
          id="product-highlighted"
          checked={highlighted}
          onCheckedChange={setHighlighted}
          label={t('product.highlighted')}
        />
        <Switch
          id="product-active"
          checked={active}
          onCheckedChange={setActive}
          label={t('product.activeLabel')}
        />
      </form>
    </Modal>
  );
}

/**
 * Stanje Stripea i gumb za sinkronizaciju.
 *
 * Ruta je postojala od početka, ali je nije bilo odakle pozvati — cijena je
 * tako mogla stajati u bazi tjednima a da nitko ne zna da nije u prodaji.
 */
function StripeSyncCard() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const preview = useStripeSyncPreview();
  const sync = useStripeSync();

  // Dok se pregled učitava ili padne, kartica se ne prikazuje: stanje Stripea
  // nije kritično za rad s paketima, a lažna poruka je gora od nikakve.
  if (!preview.data) return null;

  const { products, prices } = preview.data.pending;
  const nothingPending = products === 0 && prices === 0;

  return (
    <Card className="mb-5" as="section">
      <CardHeader
        eyebrow={t('stripe.title')}
        title={
          nothingPending
            ? t('stripe.pendingNone')
            : t('stripe.pending', { products: String(products), prices: String(prices) })
        }
        action={
          nothingPending ? undefined : (
            <Button
              size="sm"
              loading={sync.isPending}
              onClick={() =>
                sync.mutate(undefined, {
                  onSuccess: (r) =>
                    toast(
                      t('stripe.synced', {
                        products: String(r.created.products),
                        prices: String(r.created.prices),
                      }),
                      'success',
                    ),
                  onError: (error) => toast(toMutationMessage(t, error), 'danger'),
                })
              }
            >
              {t('stripe.sync')}
            </Button>
          )
        }
      />
      <p className="text-[13px] text-ink-muted">{t('stripe.help')}</p>
    </Card>
  );
}

/**
 * Obrazac za cijenu, u tri načina rada.
 *
 * `edit` namjerno ne nudi iznos, valutu ni razdoblje: cijena koja je već u
 * Stripeu ondje se ne može promijeniti, pa bi izmjena kod nas značila da
 * cjenik pokazuje jedan iznos a kartica skida drugi. Promjena iznosa ide kroz
 * `replace`, koji radi novu cijenu i gasi staru.
 */
function PriceFormModal({
  editing,
  pending,
  onClose,
  onSubmit,
}: {
  editing: NonNullable<PriceEditing>;
  pending: boolean;
  onClose: () => void;
  onSubmit: (input: AdminPriceInput | AdminPriceEdit) => void;
}) {
  const { t } = useTranslation();
  const existing = editing.mode === 'create' ? null : editing.price;
  const amountsLocked = editing.mode === 'edit';

  const [amount, setAmount] = useState<number | ''>(
    existing && editing.mode === 'replace' ? centsToEuros(existing.amountCents) : '',
  );
  const [compareAt, setCompareAt] = useState<number | ''>(
    existing?.compareAtCents ? centsToEuros(existing.compareAtCents) : '',
  );
  const [interval, setInterval] = useState<AdminPriceRow['interval']>(
    existing?.interval ?? 'one_time',
  );
  const [intervalCount, setIntervalCount] = useState<number | ''>(existing?.intervalCount ?? 1);
  const [labelHr, setLabelHr] = useState(existing?.labelHr ?? '');
  const [labelEn, setLabelEn] = useState(existing?.labelEn ?? '');
  const [sortOrder, setSortOrder] = useState<number | ''>(existing?.sortOrder ?? 0);
  const [active, setActive] = useState(existing?.active ?? true);
  const [error, setError] = useState<string | undefined>();

  function submit() {
    const shared = {
      compareAtCents: compareAt === '' ? null : eurosToCents(compareAt),
      labelHr: labelHr.trim() || null,
      labelEn: labelEn.trim() || null,
      sortOrder: sortOrder === '' ? 0 : sortOrder,
      active,
    };

    if (amountsLocked) {
      onSubmit(shared satisfies AdminPriceEdit);
      return;
    }

    if (amount === '' || amount < 0) {
      setError(t('price.amountRequired'));
      return;
    }
    setError(undefined);
    onSubmit({
      ...shared,
      amountCents: eurosToCents(amount),
      currency: 'EUR',
      interval,
      intervalCount: intervalCount === '' ? 1 : intervalCount,
    } satisfies AdminPriceInput);
  }

  const title =
    editing.mode === 'create'
      ? t('price.newTitle')
      : editing.mode === 'edit'
        ? t('price.editTitle')
        : t('price.replaceTitle');

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      closeLabel={t('a11y.closeDialog')}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" loading={pending} onClick={submit}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {amountsLocked && <p className="text-[13px] text-ink-muted">{t('price.lockedHelp')}</p>}
        {editing.mode === 'replace' && (
          <p className="rounded-md bg-bg-alt px-4 py-3 text-[13px] text-ink-muted">
            {t('price.replaceHelp')}
          </p>
        )}

        {!amountsLocked && (
          <>
            <NumberInput
              id="price-amount"
              label={t('price.amount')}
              hint={t('price.amountHelp')}
              value={amount}
              min={0}
              step={0.01}
              error={error}
              onValueChange={setAmount}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                id="price-interval"
                label={t('price.interval')}
                value={interval}
                options={(['one_time', 'month', 'quarter', 'year'] as const).map((v) => ({
                  value: v,
                  label: t(INTERVAL_KEY[v]),
                }))}
                onChange={(e) => setInterval(e.target.value as AdminPriceRow['interval'])}
              />
              <NumberInput
                id="price-interval-count"
                label={t('price.intervalCount')}
                hint={t('price.intervalCountHelp')}
                value={intervalCount}
                min={1}
                onValueChange={setIntervalCount}
              />
            </div>
          </>
        )}

        <NumberInput
          id="price-compare-at"
          label={t('price.compareAt')}
          hint={t('price.compareAtHelp')}
          value={compareAt}
          min={0}
          step={0.01}
          onValueChange={setCompareAt}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            id="price-label-hr"
            label={t('price.labelHr')}
            hint={t('price.labelHelp')}
            value={labelHr}
            onChange={(e) => setLabelHr(e.target.value)}
          />
          <Input
            id="price-label-en"
            label={t('price.labelEn')}
            value={labelEn}
            onChange={(e) => setLabelEn(e.target.value)}
          />
        </div>

        <NumberInput
          id="price-sort-order"
          label={t('product.sortOrder')}
          hint={t('product.sortOrderHelp')}
          value={sortOrder}
          min={0}
          onValueChange={setSortOrder}
        />

        <Switch
          id="price-active"
          checked={active}
          onCheckedChange={setActive}
          label={t('price.activeLabel')}
        />
      </form>
    </Modal>
  );
}
